import { useEffect, useState } from "react";
import DB_PATH from "../DB_PATH";
import { firestore, functions } from "../firebase";

export type BookingLocation = {
  type: "video" | "phone" | "in_person";
  provider?: "custom" | "zoom" | "google_meet";
  value?: string;
};

export type BookingService = {
  id: string;
  name: string;
  description?: string;
  durationMin: number;
  price: number; // major units, 0 = free
  currency: string;
  location: BookingLocation;
  active: boolean;
};

export type TimeRange = { start: string; end: string };

export type BookingConfig = {
  enabled: boolean;
  timezone: string;
  bufferMin?: number;
  minNoticeHours?: number;
  maxAdvanceDays?: number;
  slotIncrementMin?: number | null;
  services: BookingService[];
  availability: Partial<
    Record<"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun", TimeRange[]>
  >;
  overrides?: Record<string, TimeRange[]>;
  notifyEmail?: string | null;
  updatedAt?: string;
};

export type BookingSummary = {
  id: string;
  serviceId: string;
  service: { name?: string; durationMin?: number; price?: number; currency?: string };
  startUtc: string;
  endUtc: string;
  status: string;
  client: { name?: string; email?: string; notes?: string | null };
  payment?: { paidAt?: string; amountCents?: number; currency?: string } | null;
  meetingProvider?: "zoom" | "google_meet" | "custom";
  meetingJoinUrl?: string;
  // Set when the meeting sync failed, so the booking has no join link.
  // listBookings returns both; the admin table reads them to flag the row.
  meetingSyncError?: string | null;
  // Which half failed: "sync" is no join link, "email" is a link the client
  // was never sent. The functions store it so nothing reads the message text
  // to decide behavior. Absent on bookings written before it existed.
  meetingSyncErrorKind?: "email" | "sync" | null;
  meetingSyncFailedAt?: string | null;
  createdAt: string;
};

// A booking written before meetingSyncErrorKind existed carries the kind as
// an "Email: " prefix on the message. That prefix is an internal marker, so
// strip it before a coach reads the text.
export const bookingSyncErrorText = (booking: BookingSummary): string =>
  (booking.meetingSyncError || "").replace(/^Email:\s*/, "").trim();

const configRef = (communityId: string) =>
  firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection("booking")
    .doc("config");

// Live booking config for the admin form.
export const useBookingConfig = ({ communityId }: { communityId?: string }) => {
  const [config, setConfig] = useState<BookingConfig | null>(null);
  useEffect(() => {
    if (!communityId) {
      setConfig(null);
      return;
    }
    const unsub = configRef(communityId).onSnapshot(
      (doc) => setConfig(doc.exists ? (doc.data() as BookingConfig) : null),
      () => setConfig(null)
    );
    return () => unsub();
  }, [communityId]);
  return config;
};

// Merge-save the config; a Firestore trigger republishes the public projection.
export const saveBookingConfig = async (
  communityId: string,
  config: Partial<BookingConfig>
): Promise<void> => {
  await configRef(communityId).set(
    { ...config, updatedAt: new Date().toISOString() },
    { merge: true }
  );
};

export const listBookings = async (
  communityId: string,
  options: { status?: string; fromUtc?: string; limit?: number } = {}
): Promise<BookingSummary[]> => {
  const callable = functions.httpsCallable("listBookings");
  const res = await callable({ communityId, ...options });
  return (res.data as { bookings: BookingSummary[] }).bookings || [];
};

export const adminCancelBooking = async (
  communityId: string,
  bookingId: string
): Promise<void> => {
  const callable = functions.httpsCallable("adminCancelBooking");
  await callable({ communityId, bookingId });
};
